import moment from 'moment';
import humanizeUtil, { humanize } from '@utils/humanizeUtil';
import { FONT_FAMILY, isAppModule } from '@utils/constants';
import { findAxisIndex } from '@components/metrics-panel-hz/hooks/utils';
import { TIME_AXIS, VALUE_AXIS } from '@components/metrics-panel-hz/utils/constants';
import { formatAlertSummary, isCDNIssue } from '@components/alert-panel/AIAlert/hooks/utils';
import {
  AI_ALERT_EVENTS,
  AI_ALERT_MARKERS,
  AI_ALERT_PCP,
  AiAlertSeriesKeys,
  DEVICE_AXIS,
  PCP_AXIS,
} from './constants';
import { deviceSeriesKeys } from '@src/utils/aiAlerts/mapping';
import { AI_ALERT_TYPE } from '@components/ai-alerts/hooks/useGetAIAlertType';
import { metricKeys } from '@pulse/metrics';
import { HIDE_PEAK_VALUE_METRICS } from '@src/components/ai-alerts/constants';

export const legendSeries: any = [
  'All except anomaly',
  'Projected',
  'T-7',
  'Threshold',
  'Baseline',
  'PCP Drop',
  'PCT Drop',
  'Cumulative Impacted Unique Devices',
  'Total Cumulative Unique Devices',
].map((name) => ({ name }));

const getMarker = (color) => `<span style="
display: inline-block;
background-color: ${color};
width: 7px;
height: 14px;
position: relative;
top: 2px;
"></span>`;

export const eventsMarkLineOptions = (
  data,
  showImpactedUniquesSeries,
  alertType = AI_ALERT_TYPE.SPIKE,
) => {
  const eventLabel = isAppModule
    ? alertType === AI_ALERT_TYPE.DIP
      ? 'Minimum Value'
      : 'Peak Value'
    : 'Value';
  return {
    symbol: ['none', 'rect'],
    lineStyle: {
      width: 1,
      type: 'solid',
    },
    label: {
      show: false,
    },
    emphasis: {
      disabled: true,
    },
    tooltip: {
      trigger: 'item',
      // position: ['5%', '10%'],
      borderWidth: 0,
      confine: true,
      textStyle: {
        fontFamily: FONT_FAMILY,
        // width: 100,
        // height: 40,
        fontSize: 12,
        overflow: 'break',
        shadowBlur: 10,
        shadowColor: 'rgba(0, 0, 0, .2)',
        shadowOffsetX: 1,
        shadowOffsetY: 2,
        borderRadius: 4,
      },
      formatter: (params) => {
        const { alertEvent } = params.data;
        const marker = getMarker(params.borderColor);
        if (alertEvent) {
          const showPeakValue = !HIDE_PEAK_VALUE_METRICS.includes(alertEvent.metricName);
          return `
            ${marker}
            <strong> Event: ${alertEvent.eventType} </strong> <br/>
            <i style="color: #4a4a4a;">${eventLabel}: ${showPeakValue ? alertEvent.peakValue : 'NA'}</i> <br/>
            ${showImpactedUniquesSeries ? `<i style="color: #4a4a4a;">Cumulative Impacted Unique Devices: ${alertEvent.cumulativeImpactedUniquesDisplay}</i> <br/>` : ''}
            <i style="color: #4a4a4a;">Event Time: ${alertEvent.eventTime}</i> <br/>`;
        }
        return `<strong> Event: ${params.data.name} </strong>`;
      },
    },
    data,
  };
};

export const deviceAxis = {
  id: DEVICE_AXIS,
  type: 'value',
  show: false,
  splitNumber: 1,
  nameLocation: 'middle',
  position: 'right',
  axisLabel: {
    color: '#000',
    fontSize: 12,
    fontFamily: FONT_FAMILY,
    hideOverlap: true,
    showMinLabel: true,
    showMaxLabel: true,
    formatter: (value) => humanize(value),
  },
  splitLine: { show: true },
  axisTick: { show: false },
  axisLine: {
    show: false,
    lineStyle: {
      color: '#ccc',
    },
  },
};

export const pcpDropAxis = {
  id: PCP_AXIS,
  type: 'value',
  min: 0,
  max: 100,
  interval: 50,
  splitNumber: 1,
  nameLocation: 'middle',
  position: 'right',
  show: false,
  axisLabel: {
    color: '#000',
    fontSize: 12,
    fontFamily: FONT_FAMILY,
    formatter: '{value}%',
    hideOverlap: true,
  },
  splitLine: { show: true },
  axisTick: { show: true },
  axisLine: {
    show: false,
    lineStyle: {
      color: '#ccc',
    },
  },
};

// Reset alert overlay: returns the full current series array with the
// PCP Drop overlay REMOVED and other alert overlays data-cleared.
// Callers pair this with `setOption(..., { replaceMerge: ['series'] })`
// so the internal series array is fully replaced.
//
// Why remove PCP Drop specifically? It's a bar series (type:'bar').
// Empirically, echarts leaves the bar's layout footprint contributing
// to the time axis auto-fit even when we merge data:[] onto it, so the
// axis stretches to the stale alert-window timestamps. Line-type overlays
// (T-7, Projected, Baseline, Threshold, All-except-anomaly) don't have
// this problem — clearing their data is enough. Keeping their existing
// data-clear behavior avoids churn. The plot path (getAlertsOption)
// re-adds all alert series (including PCP Drop) with proper ids and
// data when the datepicker range covers the alert again.
export const resetAlertOption = (currentSeries) =>
  currentSeries
    .filter((item) => item.id !== AI_ALERT_PCP)
    .map((item) => {
      if (!AiAlertSeriesKeys.includes(item.id)) return item;
      if (item.id === AI_ALERT_EVENTS) {
        return { ...item, markLine: { ...(item.markLine || {}), data: [] } };
      }
      return { ...item, data: [] };
    });

export const getAlertsOption = ({
  alert,
  xAxis,
  yAxis,
  showImpactedUniquesSeries,
  alertType = AI_ALERT_TYPE.SPIKE,
}) => {
  const { graphSeries, severityBrushLine, uniqueDevicesSeries, metricName } = alert;
  const isPcpAlert = metricName === metricKeys.ConcurrentPlays;

  const xAxisIndex = findAxisIndex(xAxis, TIME_AXIS);
  const yAxisIndex = findAxisIndex(yAxis, VALUE_AXIS);

  const alertSeries: any = [
    {
      id: AI_ALERT_EVENTS,
      name: 'Alert events',
      type: 'line',
      markLine: eventsMarkLineOptions(severityBrushLine.data, showImpactedUniquesSeries, alertType),
      xAxisIndex,
      yAxisIndex,
    },
  ];

  graphSeries.forEach((series) => {
    const { seriesType } = series;
    if (AiAlertSeriesKeys.includes(seriesType)) {
      alertSeries.push({
        ...series,
        id: seriesType,
        xAxisIndex,
        yAxisIndex: seriesType === AI_ALERT_PCP ? findAxisIndex(yAxis, PCP_AXIS) : yAxisIndex,
      });
    }
  });

  // unique devices series
  if (!isPcpAlert && showImpactedUniquesSeries) {
    alertSeries.push(
      ...uniqueDevicesSeries.map((ud) => ({
        ...ud,
        xAxisIndex,
        yAxisIndex: findAxisIndex(yAxis, DEVICE_AXIS),
      })),
    );
  }

  return alertSeries;
};

// unit mapping for tooltip formatter;
export const alertSeriesUnitMapping = {
  [AI_ALERT_PCP]: '%',
};
// # for device series
deviceSeriesKeys.forEach((seriesKey) => {
  alertSeriesUnitMapping[seriesKey] = '';
});

export const alertsmarkLineOptions = (data, currentProductMetrics) => {
  return {
    symbol: ['none', 'diamond'],
    lineStyle: {
      width: 2,
      type: 'solid',
      color: '#CE6940',
      opacity: 0.5,
    },
    label: {
      show: false,
    },
    emphasis: {
      disabled: true,
    },
    tooltip: {
      trigger: 'item',
      // position: ['5%', '10%'],
      borderWidth: 0,
      confine: true,
      textStyle: {
        fontFamily: FONT_FAMILY,
        // width: 100,
        // height: 40,
        fontSize: 12,
        overflow: 'break',
        shadowBlur: 10,
        shadowColor: 'rgba(0, 0, 0, .2)',
        shadowOffsetX: 1,
        shadowOffsetY: 2,
        borderRadius: 4,
      },
      formatter: ({ data }) => {
        const { aiAlert } = data;
        const {
          alertId,
          eventTime,
          rootCauseDisplay,
          metricName,
          cumulativeImpactedUniques,
          peakValueDisplay,
          metricUnit,
          peakEvent,
        } = aiAlert;

        const isMinsWithXMetric = HIDE_PEAK_VALUE_METRICS.includes(metricName);
        const curMetricReverse = (currentProductMetrics[metricName] || {}).reverse;
        return `
          <div style="color: #4a4a4a;">
          <strong> AI Alert: ${alertId} </strong> <br/>
          <span>Time Alert Fired: ${eventTime}</span> <br/>
          <span>Root Cause: ${rootCauseDisplay}</span> <br/>
          ${
            !isCDNIssue(metricName, rootCauseDisplay) && !isMinsWithXMetric
              ? `<span>${curMetricReverse || !isAppModule ? 'Peak' : 'Minimum'} Value: ${peakValueDisplay}${metricUnit} at ${peakEvent?.eventType}</span> <br/>`
              : ''
          }
          ${
            cumulativeImpactedUniques
              ? `<span>Cumulative Impacted Unique Devices ${cumulativeImpactedUniques}</span>`
              : ''
          }
          </div>`;
      },
    },
    data: data,
  };
};

// chart markLine format
export const formatAlertMark = (alert, flowId) => {
  const formattedEvent = {
    aiAlert: alert,
    // markline point
    xAxis: moment(alert.eventTimestamp * 1000).format(),
    // for tooltip
    info: formatAlertSummary(alert, flowId),
    // lineStyle: alert.alertId === selectedAlertId ? { opacity: 0 } : { opacity: 0.5 }
  };
  return formattedEvent;
};

export const getAlertMarkersOption = (
  { alerts, selectedAlertId },
  params = {},
  currentProductMetrics,
  flowId,
) => {
  // hide selected alert as the line would be marked by the first event
  const filteredAlerts: any = [];

  alerts.forEach((alert) => {
    if (alert.alertId !== selectedAlertId) {
      filteredAlerts.push(formatAlertMark(alert, flowId));
    }
  });

  return {
    id: AI_ALERT_MARKERS,
    name: 'AI alerts',
    type: 'line',
    markLine: alertsmarkLineOptions(filteredAlerts, currentProductMetrics),
    xAxisIndex: 0,
    yAxisIndex: 0,
    ...params,
  };
};
